import { useEffect } from 'react';
import { HubConnectionBuilder } from '@microsoft/signalr';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiEndpoints } from '../../../constants/endpoints/apiEndpoints';

const folioKeys = [
  ['folioCollectionHotels'],
  ['folioCollectionHotelDetail'],
  ['folioCollectionHotelHistory'],
  ['folioCollectionActivityDetail']
] as const;

/**
 * FolioRequestsHub
 *
 * Listens for real-time updates to folio requests (assignment + completion)
 * and keeps the folio widget React Query caches in sync so the UI updates without refresh.
 */
export const FolioRequestsHub = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const connection = new HubConnectionBuilder()
      .withUrl(ApiEndpoints.hubs.folioRequests)
      .withAutomaticReconnect([0, 2000, 10000, 30000, 60000, 120000])
      .build();

    const handleChange = () => {
      for (const queryKey of folioKeys) {
        void queryClient.invalidateQueries({
          queryKey,
          type: 'all',
          refetchType: 'active'
        });
      }
    };

    const registerHandlers = () => {
      const handler = () => handleChange();
      connection.on('OnAssignedChanged', handler);
      connection.on('OnCompletedChanged', handler);
      connection.on('onAssignedChanged', handler);
      connection.on('onCompletedChanged', handler);
    };

    registerHandlers();

    connection
      .start()
      .then(() => {
        connection.onreconnected(() => {
          handleChange();
        });
      })
      .catch((e) => {
        Sentry.captureException(e);
      });

    return () => {
      connection.stop().catch(() => {
        // Ignore stop errors on unmount
      });
    };
  }, [queryClient]);

  return null;
};
